import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import styled from 'styled-components';

const breakpoints = {
  mobile: '768px',
  tablet: '1024px',
};

const PageBackground = styled.div`
  min-height: 100vh;
  background: #eef2f7;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0;
  }
`;

const Page = styled.div`
  width: 100%;
  max-width: 1080px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.08), 0 6px 16px rgba(15, 23, 42, 0.04);
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  overflow: hidden;

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }

  @media (max-width: ${breakpoints.mobile}) {
    border-radius: 0;
    box-shadow: none;
    min-height: 100vh;
  }
`;

const Hero = styled.section`
  position: relative;
  background-color: #1e40af;
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 0;
  overflow: hidden;
`;

const HeroImg = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;

  @media (max-width: ${breakpoints.tablet}) {
    height: auto;
    object-fit: contain;
  }
`;

const FormSide = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 2.5rem 2.5rem 1.5rem;
  background: #ffffff;
  position: relative;

  @media (max-width: ${breakpoints.tablet}) {
    padding: 2.5rem 1.5rem 1.25rem;
  }
`;

const FormCard = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
  flex: 1;
  justify-content: center;
`;

const FormHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
`;

const FormBrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const FormBrandIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FormBrandName = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.01em;
`;

const FormDesc = styled.p`
  margin: 0;
  font-size: 0.82rem;
  color: #6b7280;
`;

const RememberRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.85rem;
  color: #4b5563;
  cursor: pointer;
  user-select: none;

  & input {
    width: 16px;
    height: 16px;
    accent-color: #2563eb;
    cursor: pointer;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #9ca3af;
  font-size: 0.8rem;
  margin: 0.25rem 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e5e7eb;
  }
`;

const GoogleButton = styled.button`
  width: 100%;
  padding: 0.78rem;
  background: #ffffff;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #d1d5db;
  }
`;

const ForgotLink = styled(Link)`
  text-align: center;
  font-size: 0.85rem;
  color: #2563eb;
  text-decoration: none;
  font-weight: 500;
  margin-top: 0.25rem;

  &:hover {
    text-decoration: underline;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;

  &:hover {
    color: #4b5563;
  }
`;

const FormFooter = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.72rem;
  color: #9ca3af;
  padding-top: 1.25rem;
  border-top: 1px solid #f3f4f6;
  margin-top: 1.5rem;
  white-space: nowrap;

  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    gap: 0.5rem;
    white-space: normal;
  }
`;

const FooterCopy = styled.span`
  flex-shrink: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 0.85rem;
  flex-shrink: 0;

  & a {
    color: #6b7280;
    text-decoration: none;
    white-space: nowrap;

    &:hover {
      color: #2563eb;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
`;

const InputWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.span`
  position: absolute;
  left: 14px;
  color: #9ca3af;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.78rem 0.95rem 0.78rem 2.6rem;
  font-size: 0.92rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
  transition: all 0.15s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #2563eb;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 0.85rem;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  transition: all 0.15s ease;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(37, 99, 235, 0.35);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMsg = styled.div`
  padding: 0.6rem 0.85rem;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  font-size: 0.85rem;
  border: 1px solid #fecaca;
`;

const RegisterLink = styled(Link)`
  text-align: center;
  font-size: 0.85rem;
  color: #6b7280;
  text-decoration: none;
  margin-top: 0.5rem;

  & strong {
    color: #2563eb;
    font-weight: 600;
  }

  &:hover strong {
    text-decoration: underline;
  }
`;

const RegisterPageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  background: linear-gradient(135deg, #eff6ff 0%, #f8f9fb 100%);
`;

const RegisterCard = styled.div`
  width: 100%;
  max-width: 460px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid #f3f4f6;
  overflow: hidden;
`;

const RegisterHeader = styled.div`
  padding: 2rem 2rem 1.25rem;
  text-align: center;
`;

const RegisterTitle = styled.h2`
  margin: 0 0 0.4rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
`;

const RegisterDesc = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #6b7280;
`;

const RegisterForm2 = styled.form`
  padding: 1rem 2rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SimpleInput = styled.input`
  padding: 0.7rem 0.85rem;
  font-size: 0.9rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #2563eb;
    background: #ffffff;
  }
`;

const SimpleSelect = styled.select`
  padding: 0.7rem 0.85rem;
  font-size: 0.9rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  width: 100%;
  box-sizing: border-box;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1.25rem;
  align-items: center;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  font-size: 0.9rem;
  color: #374151;
`;

const BirthGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
`;

const FolderSvg = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" fill="#ffffff" />
  </svg>
);

const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const LockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

function HeroSection() {
  return (
    <Hero>
      <HeroImg
        src="/images/login-hero.png"
        alt="AntsNest 파일 및 이미지 서버 관리 시스템"
      />
    </Hero>
  );
}

const EyeIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {open ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

const GoogleColorIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

function Login() {
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [remember, setRemember] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleLoginClick = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/request/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, user_pwd: password }),
        credentials: 'include',
      });

      if (response.ok) {
        const userData = await response.json();
        login(userData);
        navigate('/');
      } else {
        setMessage(response.status === 401
          ? "아이디 또는 비밀번호가 잘못되었습니다."
          : "로그인에 실패하였습니다. 다시 시도해주세요.");
      }
    } catch (error) {
      setMessage("로그인 요청 중 문제가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <PageBackground>
    <Page>
      <HeroSection />
      <FormSide>
        <FormCard>
          <FormHeader>
            <FormBrandRow>
              <FormBrandIcon><FolderSvg /></FormBrandIcon>
              <FormBrandName>AntsNest</FormBrandName>
            </FormBrandRow>
            <FormDesc>파일 및 이미지 서버 관리 시스템</FormDesc>
          </FormHeader>
          <Form onSubmit={(e) => { e.preventDefault(); handleLoginClick(); }}>
            {message && <ErrorMsg>{message}</ErrorMsg>}
            <InputGroup>
              <Label htmlFor="id">아이디</Label>
              <InputWrap>
                <InputIcon><UserIcon /></InputIcon>
                <Input
                  id="id"
                  type="text"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="아이디를 입력하세요"
                  required
                />
              </InputWrap>
            </InputGroup>
            <InputGroup>
              <Label htmlFor="password">비밀번호</Label>
              <InputWrap>
                <InputIcon><LockIcon /></InputIcon>
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="비밀번호를 입력하세요"
                  required
                  style={{ paddingRight: '2.6rem' }}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "비밀번호 숨기기" : "비밀번호 표시"}
                >
                  <EyeIcon open={showPw} />
                </PasswordToggle>
              </InputWrap>
            </InputGroup>
            <RememberRow>
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              로그인 상태 유지
            </RememberRow>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "로그인 중..." : "로그인"}
            </Button>
            <Divider>또는</Divider>
            <GoogleButton type="button" disabled>
              <GoogleColorIcon />
              Google 계정으로 로그인
            </GoogleButton>
            <ForgotLink to="/register">비밀번호를 잊으셨나요?</ForgotLink>
          </Form>
        </FormCard>
        <FormFooter>
          <FooterCopy>© {currentYear} AntsNest Co., Ltd. All rights reserved.</FooterCopy>
          <FooterLinks>
            <a href="#terms">이용약관</a>
            <a href="#privacy">개인정보처리방침</a>
            <a href="#contact">문의하기</a>
          </FooterLinks>
        </FormFooter>
      </FormSide>
    </Page>
    </PageBackground>
  );
}

function RegisterForm() {
  const [username, setUsername] = useState("");
  const [registerId, setRegisterId] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [gender, setGender] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [alias, setAlias] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const validateForm = () => {
    if (registerId.length === 0) { setMessage("아이디를 입력해주세요."); return false; }
    if (username.length === 0) { setMessage("이름을 입력해주세요."); return false; }
    if (password.length === 0) { setMessage("비밀번호를 입력해주세요."); return false; }
    if (password !== confirmPassword) { setMessage("비밀번호가 일치하지 않습니다."); return false; }
    return true;
  };

  const handleSubmitRegister = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const response = await fetch('/request/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: registerId,
          user_name: username,
          user_pwd: password,
          gender,
          birth_y: birthYear,
          birth_m: birthMonth,
          birth_d: birthDay,
          email,
          phone,
          alias,
        }),
      });

      if (!response.ok) throw new Error(response.statusText);

      alert(`${username}님, 회원가입이 성공적으로 완료되었습니다.`);
      navigate('/login');
    } catch (error) {
      console.error("회원가입 실패: ", error);
      setMessage("회원가입에 실패하였습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <RegisterPageContainer>
      <RegisterCard>
        <RegisterHeader>
          <RegisterTitle>회원가입</RegisterTitle>
          <RegisterDesc>AntsNest에 오신 것을 환영합니다</RegisterDesc>
        </RegisterHeader>
        <RegisterForm2 onSubmit={handleSubmitRegister}>
          {message && <ErrorMsg>{message}</ErrorMsg>}

          <InputGroup>
            <Label htmlFor="registerId">아이디</Label>
            <SimpleInput id="registerId" type="text" value={registerId} onChange={(e) => setRegisterId(e.target.value)} required />
          </InputGroup>
          <InputGroup>
            <Label htmlFor="username">이름</Label>
            <SimpleInput id="username" type="text" value={username} onChange={(e) => setUsername(e.target.value)} required />
          </InputGroup>
          <InputGroup>
            <Label htmlFor="alias">별칭</Label>
            <SimpleInput id="alias" type="text" value={alias} onChange={(e) => setAlias(e.target.value)} required />
          </InputGroup>
          <InputGroup>
            <Label htmlFor="password">비밀번호</Label>
            <SimpleInput id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </InputGroup>
          <InputGroup>
            <Label htmlFor="confirmPassword">비밀번호 확인</Label>
            <SimpleInput id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
          </InputGroup>
          <InputGroup>
            <Label>성별</Label>
            <RadioGroup>
              <RadioLabel>
                <input type="radio" name="gender" value="1" checked={gender === '1'} onChange={(e) => setGender(e.target.value)} />
                남성
              </RadioLabel>
              <RadioLabel>
                <input type="radio" name="gender" value="2" checked={gender === '2'} onChange={(e) => setGender(e.target.value)} />
                여성
              </RadioLabel>
            </RadioGroup>
          </InputGroup>
          <InputGroup>
            <Label>생년월일</Label>
            <BirthGroup>
              <SimpleInput type="text" placeholder="YYYY" value={birthYear} onChange={(e) => setBirthYear(e.target.value)} />
              <SimpleSelect value={birthMonth} onChange={(e) => setBirthMonth(e.target.value)}>
                <option value="">월 선택</option>
                {Array.from({ length: 12 }, (_, i) => {
                  const month = (i + 1).toString().padStart(2, '0');
                  return <option key={month} value={month}>{month}월</option>;
                })}
              </SimpleSelect>
              <SimpleInput type="text" placeholder="DD" value={birthDay} onChange={(e) => setBirthDay(e.target.value)} />
            </BirthGroup>
          </InputGroup>
          <InputGroup>
            <Label htmlFor="email">이메일</Label>
            <SimpleInput id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </InputGroup>
          <InputGroup>
            <Label htmlFor="phone">전화번호</Label>
            <SimpleInput id="phone" type="text" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </InputGroup>

          <Button type="submit" disabled={isLoading}>
            {isLoading ? "가입 중..." : "회원가입"}
          </Button>

          <RegisterLink to="/login">
            이미 계정이 있으신가요? <strong>로그인</strong>
          </RegisterLink>
        </RegisterForm2>
      </RegisterCard>
    </RegisterPageContainer>
  );
}

export { RegisterForm, Login };
